import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';

import {Form, Formik} from 'formik';
import {
  includes as _includes, mapKeys as _mapKeys, omit as _omit,
  snakeCase as _snakeCase, toNumber as _toNumber,
} from 'lodash';
import NumberFormat from 'react-number-format';
import {connect, useDispatch} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';
import * as Yup from 'yup';

import {
  Button, Grid,
  IconButton, InputLabel,
} from '@material-ui/core';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';

import {useUI} from '../../../app/context/ui';

import {InputField, SelectField} from '../../../forms';
import AppHelper from '../../../helpers/AppHelper';
import {cardTypeDb, monthsDb} from '../../../models/db';
import {CustomerService, CustomerWebService} from '../../../services';
import {PaymentStyle} from './payment-style';
import {updateProperty} from "../../../app/store/mm/customerSlice";

const initialValues = {
  paymentOption: 0,
  nameOnCard: '',
  creditCardNumber: '',
  cardType: '',
  creditCardExpirationMonth: '',
  creditCardExpirationYear: '',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
};

const customerWebService = new CustomerWebService();
const customerService = new CustomerService();

const PaymentPage = (props) => {
  const {user} = props;
  const classes = PaymentStyle();
  const {snackbarUI, blockUI, dialogUI} = useUI();
  const [formInitialValues, setFormInitialValues] = useState(initialValues);
  const [cardYears, setCardYears] = useState([]);
  const formikPayments = useRef();
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const validationCreditSchema = Yup.object({
    nameOnCard: Yup.string().nullable().required('Is required'),
    creditCardNumber: Yup.number().nullable().required('Is required'),
    cardType: Yup.number().nullable().required('Is required'),
    creditCardExpirationMonth: Yup.number().nullable().required('Card month is required'),
    creditCardExpirationYear: Yup.number().nullable().required('Card year is required'),
  });


  const getInitialValues = useCallback(async () => {
    try {
      let id = location.state?.id ? location.state.id : 0;
      blockUI.current.open(true);
      customerWebService.getAccessToken();
      customerService.getAccessToken();

      let r1 =
        location.pathname === '/payment-configuration'
          ? await customerService.read(id)
          : await customerWebService.read(id);

      setFormInitialValues({
        ...initialValues,
        nameOnCard: r1.data.billingName,
      });
      blockUI.current.open(false);
    } catch (e) {
      AppHelper.checkError(e, snackbarUI);
    }
  }, [snackbarUI, blockUI, location]);

  const onSend = useCallback(
    async (values) => {
      try {
        blockUI.current.open(true);
        customerWebService.getAccessToken();
        customerService.getAccessToken();
        const valueSend = {...values};

        if (window.paytrace || window.paytrace.encryptValue) {
          const encrypted = window.paytrace.encryptValue(values.creditCardNumber);
          valueSend.creditCardNumber = encrypted;
        }

        const payload = _mapKeys(valueSend, (v, k) => _snakeCase(k));

        const updatePayment =
          user.role === 'customers'
            ? await customerWebService.updateCreditCard(payload, user.id)
            : await customerService.updateCreditCard(payload, location.state.id);

        dispatch(updateProperty({'cardType': values.cardType}));

        let dlgSettings = {
          btn: {
            close: 'Ok',
          },
          close: true,
          onConfirm: () => {
            dialogUI.current.close();
            user.role === 'customers'
              ? history.push('/web/account/payment-list')
              : history.goBack();
          },
        };

        updatePayment
          ? dialogUI.current.open(
            'Success',
            'The payment method was updated successfully',
            dlgSettings
          )
          : null;
        blockUI.current.open(false);
      } catch (e) {
        blockUI.current.open(false);
        dialogUI.current.open('Error', `${e.response.data.message}`);
        // AppHelper.checkError(e, snackbarUI);
      }
    },
    [blockUI, dialogUI, location, user, history]
  );

  const handleChangeNumCard = (event) => {
    const str = event.target.value;
    let value = str.substring(0, 1);
    if (!_includes([3, 4, 5, 6], _toNumber(value))) {
      value = null;
    }
    if (formikPayments.current) {
      formikPayments.current.setFieldValue('creditCardNumber', str);
      formikPayments.current.setFieldValue('cardType', value);
    }
  };

  useEffect(() => {
    (async function init() {
      await getInitialValues();
    })();
  }, [getInitialValues]);

  useEffect(() => {
    const year = new Date().getFullYear();
    let arrYear = [];
    for (let i = year; i < year + 10; i++) {
      arrYear.push({value: i, label: i});
    }
    setCardYears(arrYear);
  }, []);

  useEffect(() => {
    const setupKey = async () => {
      try {
        const response =
          user.role === 'customers'
            ? await customerWebService.publicKey()
            : await customerService.publicKey();

        window.paytrace.setKey(response.data);
      } catch (error) {
        console.error('Failed to load public key.', error);
      }
    };

    const loadPaytrace = () => {
      if (!window.paytrace) {
        const script = document.createElement('script');
        script.src = 'https://api.paytrace.com/assets/e2ee/paytrace-e2ee.js';
        script.onload = setupKey;
        document.body.appendChild(script);
      } else {
        setupKey();
      }
    };

    loadPaytrace();
  }, []);


  /*useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://api.paytrace.com/assets/e2ee/paytrace-e2ee.js';
    script.onload = () => {
      if (window.paytrace) {
        window.paytrace.setKeyAjax('/public_key_stg.pem');
      }
    };
    document.body.appendChild(script);
  }, []);*/

  return (
    <>
      <Formik
        innerRef={formikPayments}
        initialValues={formInitialValues}
        validationSchema={validationCreditSchema}
        onSubmit={(values) => onSend(values)}
        enableReinitialize={true}
      >
        {() => {
          return (
            <Form autoComplete='off'>
              <Grid container direction='row' justifyContent='space-between' alignItems='center'>
                <Grid item xs={3}/>
                <Grid item xs={6} className={classes.wrapperPaymentOption}>
                  <h4 className='titlePayment'>
                    <IconButton
                      color='primary'
                      component='span'
                      onClick={() => {
                        history.goBack();
                      }}
                    >
                      <KeyboardBackspaceIcon/>
                    </IconButton>
                    Payment
                  </h4>

                  <Grid container>
                    <h5 className='infoTitle'>
                      Conveniently billed to your card the next business day after your scheduled
                      mow.
                    </h5>

                    <Grid item xs={12}>
                      <InputLabel className={classes.label}>
                        Name on Card <span>*</span>
                      </InputLabel>
                      <InputField name='nameOnCard' className="pt-encrypt" fullWidth/>
                    </Grid>
                    <Grid item xs={12}>
                      <InputLabel className={classes.label}>
                        Card Number <span>*</span>
                      </InputLabel>
                      <InputField
                        name='creditCardNumber'
                        InputProps={{inputComponent: NumberFormat}}
                        onChange={handleChangeNumCard}
                        className="pt-encrypt"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <InputLabel className={classes.label}>
                        Card Type <span>*</span>
                      </InputLabel>
                      <SelectField name='cardType' data={cardTypeDb} autoFocus fullWidth/>
                    </Grid>
                    <Grid item xs={12}>
                      <InputLabel className={classes.label}>
                        Card Expiration Date <span>*</span>
                      </InputLabel>
                      <Grid container>
                        <Grid item xs={6}>
                          <SelectField
                            name='creditCardExpirationMonth'
                            data={monthsDb}
                            autoFocus
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={6} style={{paddingLeft: '8px'}}>
                          <SelectField
                            name='creditCardExpirationYear'
                            data={cardYears}
                            className="pt-encrypt"
                            autoFocus
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Button
                      type='submit'
                      variant='contained'
                      color='primary'
                      className={classes.btnSubmit}
                    >
                      SUBMIT
                    </Button>
                  </Grid>
                </Grid>
                <Grid item xs={3}/>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    user: state.user,
  };
};

export default connect(mapStateToProps)(PaymentPage);

